import { number_from_query, str_from_query } from "@/api";
import { axios_instance } from "@/api/api";
import { router } from "@/router/router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";

import { defineStore } from "pinia";
import type { Ref } from "vue";
import { computed, ref } from "vue";
import { toast } from "vue3-toastify";
import type { action } from ".";
import type { action_str, type_str } from "..";
import { use_client_detail } from "../useClientDetail";

export const createAction = async (payload: {
  agent_id: string;
  action?: action_str | "0" | "1" | "2" | "3";
  type?: type_str;
  path?: string;
  args?: string;
  recursive?: boolean;
  old_file?: string;
  new_file?: string;
  run_with?: string;
  version?: string;
  nmap_option?: { scan_type: string; intensity: number };
}): Promise<string | undefined> => {
  const data = JSON.stringify(payload);

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: "/api/v1/view/action",
    data: data,
  };

  const response = await axios_instance.request<{
    action_id: string;
    message: string;
    status: string;
  }>(config);
  return response.data.action_id;
};

export const use_action = defineStore("action", () => {
  const client_detail_store = use_client_detail();
  const actions = ref<action[]>([]);
  const total_filter = ref<number>(0);
  const total_pages = computed(() =>
    Math.max(1, Math.ceil(total_filter.value / number_from_query("size", 24))),
  );

  const loading = ref({
    create_action: false,
    get_action_history: false,
  });
  const create_action = async (payload: {
    action?: action_str | "0" | "1" | "2" | "3";
    type?: type_str;
    path?: string;
    args?: string;
    recursive?: boolean;
    old_file?: string;
    new_file?: string;
    run_with?: string;
    version?: string;
    ip?: string;
    nmap_option?: { scan_type: string; intensity: number };
  }): Promise<string | undefined> => {
    const data = JSON.stringify({
      ...payload,
      agent_id: router.currentRoute.value.params.client_id,
    });

    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: "/api/v1/view/action",
      data: data,
    };

    try {
      loading.value.create_action = true;
      const response = await axios_instance.request<{
        action_id: string;
        message: string;
        status: string;
      }>(config);
      return response.data.action_id;
    } finally {
      loading.value.create_action = false;
    }
  };

  const create_server_push_action = async (
    f: File | undefined,
    name?: string,
  ): Promise<string | undefined> => {
    if (!f) {
      toast.error("Thiếu file để tải lên");
      return;
    }

    const agent_id = router.currentRoute.value.params.client_id;
    if (!agent_id) {
      toast.error("Thiếu định danh máy trạm");
      return;
    }
    const path = str_from_query("path");
    if (!path) {
      toast.error("Thiếu đường dẫn");
      return;
    }

    const data = new FormData();
    data.append("agent_id", agent_id.toString());
    data.append("type", "explore");
    data.append("action", "server_push");
    data.append("path", path);
    data.append("file", f);
    data.append("name", name ?? f.name);

    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: "/api/v1/view/action/sever-push",
      data: data,
    };

    try {
      const response = await axios_instance.request(config);
      toast.success("Tạo tác vụ tải file lên thành công");
      return response.data.action_id;
    } catch (err) {
      toast.error(err);
      return;
    }
  };

  const get_action_history = async (
    params: {
      page?: number;
      size?: number;
      action?: action_str;
      type?: type_str;
      keyword?: string;
      status?: string[];
      distinct?: boolean;
    } = {},
  ) => {
    loading.value.get_action_history = true;
    try {
      const response = await axios_instance.post<{
        data: action[] | null;
        total_filter: number;
      }>("/api/v1/view/action/history", {
        agent_id: client_detail_store.id,
        action: params.action,
        type: params.type,
        page: params.page ?? 1,
        size: params.size ?? 24,
        keyword: params.keyword,
        status: params.status,
        distinct: params.distinct,
      });
      actions.value = response.data.data ?? [];
      total_filter.value = response.data.total_filter;
    } catch (err) {
      toast.error(err);
    } finally {
      loading.value.get_action_history = false;
    }
  };

  const get_action_detail = async (id: string): Promise<action | undefined> => {
    const config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `/api/v1/view/action/${id}`,
    };

    try {
      const response = await axios_instance.request<{ data: action }>(config);
      return response.data.data;
    } catch (error) {
      toast.error(error);
    }
  };

  const watch_for_action = async (
    action_id: string,
    func?: {
      on_start?: () => unknown;
      on_complete?: () => unknown;
      on_fail?: (error?: Error) => unknown;
    },
  ) => {
    const action = ref<action>();
    const is_watching = ref<boolean>(false);

    func?.on_start?.();
    action.value = await get_action_detail(action_id);
    if (!action.value) {
      is_watching.value = false;
      func?.on_fail?.();
      return;
    }

    if (action.value.status === "complete") {
      is_watching.value = false;
      func?.on_complete?.();
      return;
    }
    is_watching.value = true;

    setTimeout(() => {
      watch_for_action(action_id, func);
    }, 1000);
  };

  return {
    actions,
    total_filter,
    total_pages,
    loading,
    create_action,
    create_server_push_action,
    get_action_history,
    watch_for_action,
  };
});

export const fetchActionHistory = async (params: {
  agent_id: string;
  page?: number;
  size?: number;
  action?: action_str;
  type?: type_str;
  keyword?: string;
  status?: string[];
  distinct?: boolean;
}) => {
  const response = await axios_instance.post<{
    data: action[] | null;
    total_filter: number;
  }>("/api/v1/view/action/history", {
    agent_id: params.agent_id,
    action: params.action,
    type: params.type,
    page: params.page ?? 1,
    size: params.size ?? 24,
    keyword: params.keyword,
    status: params.status,
    distinct: params.distinct,
  });
  return response.data;
};

export const useActionHistoryQuery = (
  params: Ref<{
    page?: number;
    size?: number;
    action?: action_str;
    type?: type_str;
    keyword?: string;
    status?: string[];
    distinct?: boolean;
  }>,
) => {
  const client_detail_store = use_client_detail();
  return useQuery({
    queryKey: computed(() => [
      "action_history",
      client_detail_store.id,
      params.value,
    ]),
    queryFn: () =>
      fetchActionHistory({
        agent_id: client_detail_store.id,
        ...params.value,
      }),
    placeholderData: keepPreviousData,
  });
};
export const useCreateActionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["action_history"] });
    },
  });
};
